import type { WorkspaceTab } from "@/features/workspace-router/state/workspaceStore";
import type { MessageDescriptor } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import {
  Blend,
  Bot,
  Calendar as CalendarIcon,
  CheckSquare,
  Compass,
  FileText,
  LayoutDashboard,
  type LucideIcon,
  Mail,
  Share2,
  Sparkles,
  TrendingUp,
  Wallet,
  Workflow,
} from "lucide-react";

/**
 * Workspace tab config — single source of truth for the v18 top-tab bar.
 *
 * Every consumer (top tabs, breadcrumb pill, output rail, sub-tab bar,
 * router-side `setTab`) reads from this module. NEVER hard-code labels /
 * icons / routes inline — extend the maps here and the chrome picks it up.
 *
 * `route` is typed as a literal so TanStack Router `navigate({ to })` keeps
 * type-safety.
 */

export interface WorkspaceTabDef {
  readonly id: WorkspaceTab;
  /**
   * Italian fallback label kept for backwards-compat with existing consumers
   * that read ``.label`` as a plain string (WorkspaceTabs, ProjectPill,
   * ChatEmptyState, ActionFormSurface…). Phase 1A i18n only wraps the
   * extractable counterpart ``labelMsg`` below; consumers will migrate to
   * resolve via ``i18n._(def.labelMsg)`` in follow-up phases.
   */
  readonly label: string;
  /** Lingui-extractable MessageDescriptor mirror of ``label``. */
  readonly labelMsg: MessageDescriptor;
  /** Pill-style breadcrumb label (top-left ProjectPill). */
  readonly breadcrumbLabel: string;
  /** Lingui-extractable MessageDescriptor mirror of ``breadcrumbLabel``. */
  readonly breadcrumbLabelMsg: MessageDescriptor;
  readonly icon: LucideIcon;
  readonly route:
    | "/workspace/conversations"
    | "/workspace/calendar"
    | "/workspace/finance"
    | "/workspace/sales"
    | "/workspace/documents"
    | "/workspace/technical"
    | "/workspace/work"
    | "/workspace/canvases"
    | "/workspace/dashboard"
    | "/workspace/automations"
    | "/workspace/flows"
    // Ando Connect hub (the "shared" tab points here).
    | "/workspace/connect"
    // Condivisioni — chat shares hub (Sep 2026; was ``/workspace/shared``).
    | "/workspace/shares";
  readonly defaultSubTab: string;
  /**
   * Where the tab lives in the shell (Jul 2026 IA slim-down):
   *   - ``"top"`` (default) → the horizontal workspace tab bar.
   *   - ``"spazi"`` → the "Spazi" section at the bottom of the left project
   *     sidebar, keeping the top bar to the 7 daily work areas.
   */
  readonly placement?: "top" | "spazi";
}

export const WORKSPACE_TAB_DEFS = [
  {
    id: "conversations",
    label: "Conversazioni",
    labelMsg: msg`Conversazioni`,
    breadcrumbLabel: "Conversazioni",
    breadcrumbLabelMsg: msg`Conversazioni`,
    icon: Mail,
    route: "/workspace/conversations",
    defaultSubTab: "all",
  },
  {
    id: "calendar",
    label: "Calendario",
    labelMsg: msg`Calendario`,
    breadcrumbLabel: "Calendario",
    breadcrumbLabelMsg: msg`Calendario`,
    icon: CalendarIcon,
    route: "/workspace/calendar",
    defaultSubTab: "today",
  },
  {
    id: "documents",
    label: "Documenti",
    labelMsg: msg`Documenti`,
    breadcrumbLabel: "Documenti",
    breadcrumbLabelMsg: msg`Documenti`,
    icon: FileText,
    route: "/workspace/documents",
    defaultSubTab: "all",
  },
  {
    id: "finance",
    // ``Amministrazione`` (UI/UX audit 2026-05-30): the SMB-italian lessico
    // for banca + fatture + fisco + CRM. "Finanza" reads CFO-jargon and
    // accorpa concept the user keeps mentally separate. The URL slug stays
    // ``/workspace/finance`` so deep-links + analytics don't regress.
    label: "Amministrazione",
    labelMsg: msg`Amministrazione`,
    breadcrumbLabel: "Amministrazione",
    breadcrumbLabelMsg: msg`Amministrazione`,
    icon: Wallet,
    route: "/workspace/finance",
    defaultSubTab: "overview",
  },
  {
    id: "sales",
    label: "Sales",
    labelMsg: msg`Sales`,
    breadcrumbLabel: "Sales",
    breadcrumbLabelMsg: msg`Sales`,
    icon: TrendingUp,
    route: "/workspace/sales",
    defaultSubTab: "campaigns",
  },
  {
    id: "technical",
    label: "2D/3D Design",
    labelMsg: msg`2D/3D Design`,
    breadcrumbLabel: "2D/3D Design",
    breadcrumbLabelMsg: msg`2D/3D Design`,
    icon: Compass,
    route: "/workspace/technical",
    // CAD Big Bang (Jun 12, 2026) — the legacy ``"drawings"`` value no
    // longer exists; the hub now opens on 2D drawings by default and
    // surfaces 3D models / assemblies via dedicated tabs. The store
    // reducer below maps the legacy value to ``drawings_2d`` so
    // deep-links + persisted state from before the split keep working.
    defaultSubTab: "drawings_2d",
  },
  {
    id: "work",
    label: "To Do",
    labelMsg: msg`To Do`,
    breadcrumbLabel: "To Do",
    breadcrumbLabelMsg: msg`To Do`,
    icon: CheckSquare,
    route: "/workspace/work",
    defaultSubTab: "today",
  },
  // ── May 23, 2026 IA cleanup ────────────────────────────────────────
  // Canvas / Automazioni / Condivisioni promoted from the avatar
  // drawer's "Studio" block into first-class workspace tabs. UX audit
  // confirmed daily-to-weekly usage with chat context — they belong
  // alongside Conversazioni/Calendario, not inside the customization
  // surface. The Studio pill keeps only the rare meta-config
  // surfaces (Modes / Brand / Voice / Templates).
  {
    id: "canvases",
    // Brand name — kept verbatim across locales.
    label: "Canvas",
    labelMsg: msg`Canvas`,
    breadcrumbLabel: "Canvas",
    breadcrumbLabelMsg: msg`Canvas`,
    icon: Sparkles,
    route: "/workspace/canvases",
    defaultSubTab: "all",
    placement: "spazi",
  },
  // May 26, 2026 — Dashboard promoted from the floating ``OutputRail``
  // to a first-class tab. The rail (deleted in the same commit) was
  // duplicating Canvas chrome AND hiding Dashboard behind a low-
  // discoverability secondary nav. See workspace.ts in @ando/core for
  // the wire-level rationale.
  {
    id: "dashboards",
    // Jun 2026 — renamed "Dashboard" → "Analisi": one thematic home for
    // data-viz. Holds an internal toggle Dashboard ⇄ Report (built-your-own
    // vs delivered analytical reports) instead of two separate top tabs.
    label: "Analisi",
    labelMsg: msg`Analisi`,
    breadcrumbLabel: "Analisi",
    breadcrumbLabelMsg: msg`Analisi`,
    icon: LayoutDashboard,
    route: "/workspace/dashboard",
    defaultSubTab: "all",
    placement: "spazi",
  },
  {
    id: "automations",
    label: "Automazioni",
    labelMsg: msg`Automazioni`,
    breadcrumbLabel: "Automazioni",
    breadcrumbLabelMsg: msg`Automazioni`,
    icon: Bot,
    route: "/workspace/automations",
    defaultSubTab: "active",
    placement: "spazi",
  },
  // Jun 2026 — Hive Flows: the visual, concept-level automation builder.
  // Sits right after Automazioni (its more powerful sibling).
  {
    id: "flows",
    // ``Flows`` / ``Hive Flows`` are product names — left as brand strings.
    label: "Flows",
    labelMsg: msg`Flows`,
    breadcrumbLabel: "Hive Flows",
    breadcrumbLabelMsg: msg`Hive Flows`,
    icon: Workflow,
    route: "/workspace/flows",
    defaultSubTab: "all",
    placement: "spazi",
  },
  {
    // Internal id kept as ``shared`` (a persisted ``WorkspaceTab`` enum value —
    // renaming it would invalidate saved "restore-where-you-left" state). The
    // user-facing surface is **Ando Connect**: Spaces for inter-company
    // collaboration + customer chatbots. Chat shares live in ``shares`` below.
    id: "shared",
    // Brand name — kept verbatim across locales (like Canvas / Flows).
    label: "Connect",
    labelMsg: msg`Connect`,
    breadcrumbLabel: "Connect",
    breadcrumbLabelMsg: msg`Connect`,
    icon: Blend,
    route: "/workspace/connect",
    defaultSubTab: "all",
    placement: "spazi",
  },
  // Sep 2026 — Condivisioni: conversations and answers sent to a link, a
  // colleague, an email or WhatsApp. Its own rail entry, its own vocabulary
  // (Inviate / Ricevute), so it never collides with Connect's Spaces.
  {
    id: "shares",
    label: "Condivisioni",
    labelMsg: msg`Condivisioni`,
    breadcrumbLabel: "Condivisioni",
    breadcrumbLabelMsg: msg`Condivisioni`,
    icon: Share2,
    route: "/workspace/shares",
    defaultSubTab: "mine",
    placement: "spazi",
  },
] as const satisfies readonly WorkspaceTabDef[];

// ``as const`` narrows each entry to a literal shape without the optional
// ``placement`` key, so widen to ``WorkspaceTabDef[]`` before filtering on it.
const ALL_TAB_DEFS: readonly WorkspaceTabDef[] = WORKSPACE_TAB_DEFS;

/** Tabs shown in the horizontal top bar (the 7 daily work areas). */
export const TOP_TAB_DEFS = ALL_TAB_DEFS.filter((t) => t.placement !== "spazi");

/** Tabs relocated to the "Spazi" section of the left project sidebar. */
export const SPAZI_TAB_DEFS = ALL_TAB_DEFS.filter((t) => t.placement === "spazi");

export type WorkspaceTabId = (typeof WORKSPACE_TAB_DEFS)[number]["id"];

const TAB_DEFS_BY_ID = new Map<WorkspaceTab, WorkspaceTabDef>(
  WORKSPACE_TAB_DEFS.map((t) => [t.id, t]),
);

export function getWorkspaceTabDef(id: WorkspaceTab): WorkspaceTabDef | undefined {
  return TAB_DEFS_BY_ID.get(id);
}

/**
 * Reverse map ``route`` → tab id. Built once at module init from
 * ``WORKSPACE_TAB_DEFS`` so adding a new tab automatically wires the
 * route-→-tab sync path without touching every consumer. June 10, 2026.
 */
const TAB_ID_BY_ROUTE = new Map<string, WorkspaceTab>(
  WORKSPACE_TAB_DEFS.map((t) => [t.route, t.id]),
);

/**
 * Map a router pathname to the workspace tab that owns it.
 *
 * Used by both ends of the route↔store sync:
 *   - ``_authed/index.tsx`` smart redirect (restore-where-you-left)
 *   - ``useSyncTabFromRoute`` (route = single source of truth)
 *
 * Matching is **prefix-based on the canonical tab route** so deep paths
 * (``/workspace/finance/banking/abc``) still resolve to the parent tab.
 * Falls back to ``null`` for paths outside ``/workspace/<tab>/...``
 * (modals, integrations hub, settings) — callers can then no-op
 * instead of forcing a wrong tab.
 */
export function getWorkspaceTabIdFromPathname(pathname: string): WorkspaceTab | null {
  // Normalise once: strip any trailing slash so ``/workspace/calendar``
  // and ``/workspace/calendar/`` are indistinguishable to the lookup.
  // Without this the trailing-slash form would miss the direct map and
  // fall through to the prefix loop, which is slower and semantically
  // surprising for a SOTA reader.
  const normalized =
    pathname.length > 1 && pathname.endsWith("/") ? pathname.slice(0, -1) : pathname;
  // Direct hit first (cheapest path, covers the canonical case).
  const exact = TAB_ID_BY_ROUTE.get(normalized);
  if (exact !== undefined) return exact;
  // Prefix match for nested routes (e.g. ``/workspace/calendar/event/123``).
  // Append ``/`` to the candidate so ``/workspace/calendar`` does NOT
  // accidentally match ``/workspace/calendars-archive``.
  for (const [route, id] of TAB_ID_BY_ROUTE) {
    if (normalized.startsWith(`${route}/`)) return id;
  }
  return null;
}

// ─── Sub-tabs ────────────────────────────────────────────────────────────

export interface SubTabDef {
  readonly id: string;
  /**
   * Chip label — accepts a plain string (legacy inline construction in
   * route hubs that build dynamic chip arrays) OR a Lingui
   * ``MessageDescriptor`` (the static ``WORKSPACE_SUB_TABS`` registry
   * below, which Lingui can extract). ``SubTabBar`` resolves both forms
   * uniformly via ``i18n._(chip.label)`` at render time — passing a
   * descriptor through ``i18n._`` returns its translation; passing a
   * string returns the same string. Brand names (Google Drive, OneDrive,
   * SharePoint, Slack, Teams, PEC) are still wrapped in ``msg`` so the
   * catalog records them as locale-stable rather than missing.
   */
  readonly label: string | MessageDescriptor;
  readonly badge?: number | string;
}

export const WORKSPACE_SUB_TABS: Record<WorkspaceTab, readonly SubTabDef[]> = {
  conversations: [
    { id: "all", label: msg`Tutti`, badge: 7 },
    { id: "email", label: msg`Email`, badge: 4 },
    { id: "slack", label: msg`Slack`, badge: 1 },
    { id: "teams", label: msg`Teams`, badge: 1 },
    { id: "pec", label: msg`PEC`, badge: 1 },
  ],
  calendar: [
    { id: "today", label: msg`Oggi` },
    { id: "week", label: msg`Settimana` },
    { id: "month", label: msg`Mese` },
    { id: "all", label: msg`Tutti` },
  ],
  // SMB-italian lessico (UI/UX audit 2026-05-30): chip labels follow the
  // user vocabulary — "Banking" / "SDI" / "F24 scadenze" were vendor /
  // protocol jargon, swapped to "Banca" / "Vendite" / "Fisco". Chip
  // ``id`` values stay frozen so URL deep-links + analytics don't
  // regress. Mirrors the finance.tsx inline ``chips`` array (kept
  // duplicated for the dynamic ``badge`` calculation per-render).
  finance: [
    { id: "overview", label: msg`Panoramica` },
    { id: "banking", label: msg`Banca` },
    { id: "sdi", label: msg`Fatture` },
    { id: "f24", label: msg`Fisco` },
    { id: "cashflow", label: msg`Cash flow` },
  ],
  sales: [
    { id: "overview", label: msg`Panoramica` },
    // Native CRM spine (Jul 2026, tasks/sales_crm_plan.md Phase 1): deals on a
    // kanban pipeline + first-class contacts/companies. The outbound funnel
    // (Campagne/Lead) converts into it via "Converti in opportunità".
    { id: "pipeline", label: msg`Pipeline` },
    { id: "contacts", label: msg`Contatti` },
    { id: "companies", label: msg`Aziende` },
    { id: "campaigns", label: msg`Campagne` },
    { id: "leads", label: msg`Lead` },
    { id: "suppression", label: msg`Esclusioni` },
  ],
  documents: [
    // v18 mockup r3308 — filter chips by *provider*, not by content kind.
    // The trailing two chips (Email allegati / Upload) extend the mockup so
    // every BE ``source_type`` has a home; users dropping a file from chat
    // / email still need a way to find it.
    { id: "all", label: msg`Tutti` },
    { id: "google_drive", label: msg`Google Drive` },
    { id: "onedrive", label: msg`OneDrive` },
    { id: "sharepoint", label: msg`SharePoint` },
    { id: "filesystem", label: msg`Cartelle e NAS` },
    { id: "email_attachment", label: msg`Allegati email` },
    { id: "manual_upload", label: msg`Caricati` },
  ],
  technical: [
    // May 28, 2026 — the standalone "Libreria DXF" sub-tab was removed:
    // the Tecnico hub itself now IS the DXF library (sidebar categories,
    // search, preview, BOM export, revisions). Each chip below is a real
    // cut of the same dataset, not a separate route.
    // CAD Big Bang (Jun 12, 2026 SOTA) — "Disegni" split into 2D/3D
    // discriminator + "Assiemi" entry-point so STEP assembly trees are
    // discoverable. The hub body resolves the format via the analyzer's
    // ``cad_format`` field (DRY: same value the BE stores in
    // ``content_items.cad_analysis``).
    // Jul 2026 IA cleanup — dropped Assiemi/Revisioni/Distinte:
    // the customer's mental buckets are 2D drawings, 3D models,
    // and (new) DFM rule library. Assembly detection stays as a
    // filter inside 3D; revisions surface via ``updated_at`` on
    // the card; BOM is a per-file action from the preview.
    { id: "drawings_2d", label: msg`Disegni 2D` },
    { id: "models_3d", label: msg`Modelli 3D` },
    { id: "dfm_rules", label: msg`Regole progettazione` },
    // Sprint 2 (Jul 10, 2026, issue #903) — "Fonti prezzi" is the
    // catch-all hub for pricing knowledge Ando joins to a CAD BOM at
    // quote time. Today only the "manual upload" source ships
    // (price_list_items); tomorrow Drive/SharePoint live-parse, ERP
    // (SAP/FiC/SF/HubSpot) live query, and tenant DB via NL-to-SQL
    // register into the same rail — see
    // ``ando-agent/src/services/price_lookup/resolver.py``. The name
    // is intentionally "Fonti" (not "Listini") so it stays honest as
    // the non-upload sources land.
    { id: "price_lists", label: msg`Fonti prezzi` },
  ],
  work: [
    { id: "today", label: msg`Oggi` },
    { id: "upcoming", label: msg`In arrivo` },
    { id: "overdue", label: msg`In ritardo` },
    { id: "agents", label: msg`Agenti AI` },
  ],
  canvases: [
    { id: "all", label: msg`Tutti` },
    { id: "mine", label: msg`Miei` },
    { id: "shared", label: msg`Condivisi` },
  ],
  // Sub-tabs mirror Canvas (the other AI-output surface). "Tutti"
  // lists every dashboard the user can read; "Miei" filters to owned;
  // "Condivisi" surfaces ones shared TO the user (parity with Canvas
  // semantics — same store-level filter applies).
  dashboards: [
    { id: "all", label: msg`Tutti` },
    { id: "mine", label: msg`Miei` },
    { id: "shared", label: msg`Condivisi` },
  ],
  automations: [
    { id: "active", label: msg`Attive` },
    { id: "paused", label: msg`In pausa` },
    { id: "draft", label: msg`Bozze` },
    { id: "runs", label: msg`Esecuzioni` },
  ],
  flows: [
    { id: "all", label: msg`Tutti` },
    { id: "active", label: msg`Attivi` },
    { id: "draft", label: msg`Bozze` },
  ],
  // Ando Connect (the "shared" slot) renders its own "I miei Spazi" /
  // "Spazi a cui sei invitato" sections in the hub — no sub-tab chip bar.
  // Empty ⇒ ``SubTabBar`` renders nothing (it early-returns on a zero-length list).
  shared: [],
  // Condivisioni renders its own Inviate / Ricevute switch (the URL ``sub``
  // still drives it, so deep links land on the right lane).
  shares: [],
};

// ── OutputRail removed May 26, 2026 ──────────────────────────────────────
// Canvas + Dashboard are now first-class ``WORKSPACE_TAB_DEFS`` entries.
// The floating right-edge rail was deleted because (a) Canvas was already
// a tab (chrome duplication) and (b) "secondary nav as a floating
// icon-only rail" is harder to discover than a labeled top tab. Any
// historical ``OUTPUT_RAIL_ENTRIES`` / ``RailEntryId`` import should
// migrate to ``WORKSPACE_TAB_DEFS`` + tab id ``"canvases"`` /
// ``"dashboards"``.
